import { ChangeEvent, FC, useCallback, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import './BrDualRangeSlider.scss';
import BrButton from '@components/BrButton';
import ColorStyles from '@components/ColorStyles';

interface MultiRangeSliderProps {
  min: number;
  max: number;
  selectedMin: number;
  selectedMax: number;
  stepValue: number;
  onDone: ({ min, max }: {min: number, max: number}) => void;
}

const BrDualRangeSlider: FC<MultiRangeSliderProps> = ({
  min = 0,
  max = 1,
  selectedMin = 0,
  selectedMax = 1,
  stepValue = 0.01,
  onDone,
}) => {
  const [minVal, setMinVal] = useState(selectedMin);
  const [maxVal, setMaxVal] = useState(selectedMax);
  const [minValInp, setMinValInp] = useState(selectedMin);
  const [maxValInp, setMaxValInp] = useState(selectedMax);
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);
  const range = useRef<HTMLDivElement>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max],
  );

  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value);
      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(+minValRef.current.value);
      const maxPercent = getPercent(maxVal);
      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);

  const sliderMinChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(+event.target.value, maxVal - stepValue);
    setMinVal(value);
    setMinValInp(value);
    event.target.value = value.toString();
  };

  const sliderMaxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(+event.target.value, (+minVal) + stepValue);
    setMaxVal(value);
    setMaxValInp(value);
    event.target.value = value.toString();
  };

  const handleMinValChange = (e: ChangeEvent<HTMLInputElement>) => {
    const regex = /^-?[0-9]\d*(\.\d+)?$/;
    if (e.target.value === '' || regex.test(e.target.value)) {
      setMinValInp(+e.target.value);
    }
  };

  const handleMaxValChange = (e: ChangeEvent<HTMLInputElement>) => {
    const regex = /^-?[0-9]\d*(\.\d+)?$/;
    if (e.target.value === '' || regex.test(e.target.value)) {
      setMaxValInp(+e.target.value);
    }
  };

  const handleMinValOnBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const val = +e.target.value;
    if (val >= min && val < maxVal) { setMinVal(val); } else { setMinValInp(minVal); }
  };

  const handleMaxValOnBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const val = +e.target.value;
    if (val <= max && val > minVal) { setMaxVal(val); } else { setMaxValInp(maxVal); }
  };

  const onSubmitVal = () => {
    onDone({ min: minVal, max: maxVal });
  };

  return (
    <div className="br-d-r-slider-container">
      <div className="br-d-r-slider">
        <input
          type="range"
          min={min}
          max={max}
          step={stepValue}
          value={minVal}
          ref={minValRef}
          onChange={sliderMinChange}
          className={classnames('br-d-r-slider-thumb zindex-3', {
            'zindex-5': minVal > max - 100,
          })}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={stepValue}
          value={maxVal}
          ref={maxValRef}
          onChange={sliderMaxChange}
          className="br-d-r-slider-thumb zindex-4"
        />

        <div className="br-d-r-track">
          <div className="br-d-r__track" />
          <div ref={range} className="br-d-r__range" />
        </div>
      </div>

      <div className="br-d-r-inputs">
        <input
          placeholder="Min"
          type="number"
          step={stepValue}
          value={minValInp}
          onChange={handleMinValChange}
          onBlur={handleMinValOnBlur}
        />
        <input
          placeholder="Max"
          type="number"
          step={stepValue}
          value={maxValInp}
          onChange={handleMaxValChange}
          onBlur={handleMaxValOnBlur}
        />
        <BrButton
          title="Done"
          onClick={onSubmitVal}
          colorStyle={ColorStyles.BGBLUE}
          tooltipText="Done"
        />
      </div>
    </div>
  );
};

export default BrDualRangeSlider;
